import { useCallback, useEffect, useState } from "react";
import {
  addProjectMember,
  listProjectMembers,
  removeProjectMember,
  updateProjectMember,
  type ProjectMember,
  type ProjectRole,
} from "../lib/api";

/**
 * Project members dialog (Plan §21 Phase 5 RBAC v1).
 *
 * Shows the membership list, lets admins/owners invite by email + change
 * roles + remove. Read-only for editors and viewers.
 *
 * Roles:
 *   owner > admin > editor > viewer
 *
 * Invite flow v1: invitee MUST already have a UAIP account (we look them
 * up by email server-side). v2 will support pending invites by email.
 */
export function ProjectMembersDialog(props: {
  projectId: string;
  projectName: string;
  onClose: () => void;
}): JSX.Element {
  const { projectId, projectName, onClose } = props;
  const [members, setMembers] = useState<ProjectMember[] | null>(null);
  const [yourRole, setYourRole] = useState<ProjectRole | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<ProjectRole>("editor");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await listProjectMembers(projectId);
      setMembers(r.members);
      setYourRole(r.yourRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const canManage = yourRole === "owner" || yourRole === "admin";

  async function handleInvite(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      await addProjectMember(projectId, inviteEmail.trim(), inviteRole);
      setInviteEmail("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleRoleChange(m: ProjectMember, role: ProjectRole): Promise<void> {
    if (m.role === role) return;
    setBusy(true);
    setError(null);
    try {
      await updateProjectMember(projectId, m.userId, role);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(m: ProjectMember): Promise<void> {
    if (!window.confirm(`Remove ${m.email ?? m.userId} from "${projectName}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      await removeProjectMember(projectId, m.userId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Members of ${projectName}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <header className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Members</h2>
            <p className="mt-0.5 text-xs text-slate-400">
              {projectName}
              {yourRole && (
                <>
                  {" · "}
                  <span className="text-slate-300">your role: {yourRole}</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && (
          <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}

        {members === null ? (
          <p className="text-sm text-slate-400">Loading members…</p>
        ) : (
          <ul className="mb-4 divide-y divide-slate-800 rounded-md border border-slate-800">
            {members.map((m) => (
              <li
                key={m.userId}
                className="flex items-center gap-3 px-3 py-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-100 truncate">
                    {m.name ?? m.email ?? m.userId}
                  </div>
                  {m.email && (
                    <div className="text-xs text-slate-500 truncate">{m.email}</div>
                  )}
                </div>
                {canManage && m.role !== "owner" ? (
                  <select
                    value={m.role}
                    disabled={busy}
                    onChange={(e) =>
                      void handleRoleChange(m, e.target.value as ProjectRole)
                    }
                    className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
                  >
                    <option value="viewer">viewer</option>
                    <option value="editor">editor</option>
                    <option value="admin">admin</option>
                    <option value="owner">owner</option>
                  </select>
                ) : (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${roleClass(m.role)}`}
                  >
                    {m.role}
                  </span>
                )}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => void handleRemove(m)}
                    disabled={busy}
                    className="rounded border border-rose-800 px-2 py-0.5 text-xs text-rose-300 hover:bg-rose-900/30 disabled:opacity-50"
                  >
                    remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage ? (
          <form onSubmit={handleInvite} className="flex flex-col gap-2">
            <label className="text-xs font-medium text-slate-300">
              Invite by email
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as ProjectRole)}
                className="rounded-md border border-slate-700 bg-slate-950 px-2 py-2 text-sm text-slate-200"
              >
                <option value="viewer">viewer</option>
                <option value="editor">editor</option>
                <option value="admin">admin</option>
              </select>
              <button
                type="submit"
                disabled={busy || !inviteEmail.trim()}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Inviting…" : "Invite"}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              v1: the invitee must already have a UAIP account (sign in with
              Google/GitHub once). v2 will support pending invites by email.
            </p>
          </form>
        ) : (
          <p className="text-xs text-slate-500">
            You're a {yourRole ?? "member"} on this project — read-only access
            to the member list. Ask an admin or owner to invite or change
            roles.
          </p>
        )}
      </div>
    </div>
  );
}

function roleClass(role: ProjectRole): string {
  switch (role) {
    case "owner":
      return "bg-amber-900/40 text-amber-200 border border-amber-700/60";
    case "admin":
      return "bg-indigo-900/50 text-indigo-200 border border-indigo-700/70";
    case "editor":
      return "bg-emerald-900/40 text-emerald-200 border border-emerald-700/60";
    case "viewer":
      return "bg-slate-800 text-slate-300 border border-slate-700";
  }
}
